/**
 * Fabrique du service vidéo — la COUTURE qui permettra de brancher un SFU auto-hébergé
 * sans toucher à l'interface ni aux composants.
 *
 * Le maillage WebRTC (`'mesh'`, défaut) tient jusqu'à ~8 participants : chacun envoie sa
 * vidéo à chacun des autres. Pour une classe de 8 à 20 il faut un SFU (chacun n'envoie
 * qu'UN flux vers le serveur, qui le redistribue) — `'sfu'` est le transport prévu pour
 * ça (colonne `video_rooms.transport`, migration 0255). Il N'EST PAS implémenté : demander
 * `'sfu'` lève `VideoTransportUnavailableError` (l'UI affiche « transport indisponible »)
 * tant qu'aucune implémentation n'est branchée ici.
 *
 * Brancher un SFU = (1) écrire une classe `LiveKitVideoService implements VideoService`,
 * (2) lui faire passer `video-service.contract.ts`, (3) l'ajouter dans le `switch` ci-dessous.
 * Procédure et déploiement : `docs/visio-sfu-selfhost.md`.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { WebRtcVideoService } from './webrtc-video-service';
import type { VideoService, VisioRole } from './video-service';

export type VideoTransport = 'mesh' | 'sfu';

export const VIDEO_TRANSPORTS: readonly VideoTransport[] = ['mesh', 'sfu'];

/** Le transport demandé n'a pas (encore) d'implémentation branchée. */
export class VideoTransportUnavailableError extends Error {
  constructor(readonly transport: string) {
    super(`transport vidéo indisponible : ${transport}`);
    this.name = 'VideoTransportUnavailableError';
  }
}

/** Valeur inconnue (colonne `transport` corrompue, salle d'une version future) → maillage. */
export function normalizeVideoTransport(value: unknown): VideoTransport {
  return value === 'sfu' ? 'sfu' : 'mesh';
}

export interface CreateVideoServiceOptions {
  transport?: VideoTransport;
  me: { userId: string; role: VisioRole; displayName: string };
  db: SupabaseClient;
  supabaseUrl: string;
  /** Id serveur de l'hôte de la salle (le tuteur) — cf. `WebRtcVideoServiceOptions`. */
  hostUserId?: string;
}

export function createVideoService(opts: CreateVideoServiceOptions): VideoService {
  const transport = opts.transport ?? 'mesh';
  switch (transport) {
    case 'mesh':
      return new WebRtcVideoService(opts.me, opts.db, opts.supabaseUrl, {
        hostUserId: opts.hostUserId,
      });
    case 'sfu':
      throw new VideoTransportUnavailableError(transport);
    default:
      throw new VideoTransportUnavailableError(String(transport));
  }
}
